import {useEffect, useState} from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {
    CancelEvent,
    TableCancelGroup,
} from "crqlar-react-common/dist/models/floorplan/floorplanEvents.dto";
import {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import EventsCancelDialogTable from "./EventsCancelDialogTable";
import PreviewProfileInformation from "./PreviewProfileInformation";
import CurrentStayInformation from "./CurrentStayInformation";

interface Props {
    open: boolean;
    event?: CancelEvent;
    cancelByTable?: TableCancelGroup[];
    guestLabel?: string;
    accommodationStayLabel?: string;
    restaurantLabel?: string;
    onReservationsChanged?: () => void;
    onOpenReservation?: (reservation: Reservation) => void;
    onOpenAccommodationReservation?: (reservation: Reservation, profile?: Profile) => void;
    onBodyReady?: () => void;
    onClose: () => void;
}

const EventsCancelDialog = ({
                                open,
                                event,
                                cancelByTable = [],
                                guestLabel,
                                accommodationStayLabel,
                                restaurantLabel,
                                onReservationsChanged,
                                onOpenReservation,
                                onOpenAccommodationReservation,
                                onBodyReady,
                                onClose,
                            }: Props) => {
    const {t} = useTranslation();
    // Mirror the update dialog's defer: render shell + spinner immediately so
    // a click on a long-stay row gets visible feedback even when the body
    // mount is non-trivial.
    const [bodyReady, setBodyReady] = useState(false);

    useEffect(() => {
        if (!open) {
            setBodyReady(false);
            return;
        }
        const timer = setTimeout(() => {
            setBodyReady(true);
            onBodyReady?.();
        }, 0);
        return () => clearTimeout(timer);
    }, [onBodyReady, open]);

    if (!event) return null;

    if (!bodyReady) {
        // Preview cards are cheap to render and show the operator who/what they
        // just clicked on. Only the outstanding-reservations table is heavy
        // enough to need a spinner. JSX structure matches the full render so
        // React reconciles the cards instead of remounting them.
        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{fontWeight: 700}}>
                    {t("Cancel reservations")}
                </DialogTitle>
                <DialogContent sx={{display: "flex", flexDirection: "column", overflow: "hidden", p: 0}}>
                    <Stack gap={2} sx={{px: 3, pt: 2, pb: 2}}>
                        <PreviewProfileInformation
                            event={event}
                            guestLabel={guestLabel}
                            accommodationStayLabel={accommodationStayLabel}
                            onOpenAccommodationReservation={onOpenAccommodationReservation}
                        />
                        <CurrentStayInformation event={event} onOpenReservation={onOpenReservation}/>
                    </Stack>
                    <Box sx={{
                        minHeight: 200,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1.5,
                    }}>
                        <CircularProgress/>
                        <Typography variant="body2" color="text.secondary">
                            {t("Loading...")}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>{t("Close")}</Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{fontWeight: 700}}>
                {t("Cancel reservations")}
            </DialogTitle>
            <DialogContent sx={{display: "flex", flexDirection: "column", overflow: "hidden", p: 0}}>
                {/* Top section: guest profile + departure summary cards. */}
                <Stack gap={2} sx={{px: 3, pt: 2, pb: 2}}>
                    <PreviewProfileInformation
                        event={event}
                        guestLabel={guestLabel}
                        accommodationStayLabel={accommodationStayLabel}
                        onOpenAccommodationReservation={onOpenAccommodationReservation}
                    />
                    <CurrentStayInformation event={event} onOpenReservation={onOpenReservation}/>
                </Stack>

                {/* Outstanding reservations — the table owns its own cancel/confirm flow. */}
                <Stack gap={1} sx={{minHeight: 0, flex: 1}}>
                    <Typography sx={{fontWeight: 700, px: 3}}>
                        {t("Outstanding restaurant reservations")}
                    </Typography>
                    <Box sx={{minHeight: 0, overflowY: "auto"}}>
                        <EventsCancelDialogTable
                            cancelByTable={cancelByTable}
                            restaurantLabel={restaurantLabel}
                            onReservationsChanged={onReservationsChanged}
                            onOpenReservation={onOpenReservation}
                            onAllCancelled={onClose}
                        />
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t("Close")}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default EventsCancelDialog;
